import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import { BillCaptchaCheckRes } from '@/services/mobi-api/bill/types'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { moneyFormatter } from '@/utils/helpers'
import useIntl from '@/utils/intl'
import { Download, Loop } from '@mui/icons-material'
import { TextField } from '@mui/material'
import classNames from 'classnames'
import Image from 'next/image'
import React, { FunctionComponent, useEffect, useReducer } from 'react'

interface Props {
  onClick: (payload: CreateInvoiceReq) => void
  type: string
}

type LookupStep = 'phone' | 'captcha' | 'amount'

interface State {
  number?: string
  paymentAmount?: string
  numberError?: string
  amountError?: boolean
  checkBill?: boolean
  lookupStep?: LookupStep
  lookupLoading?: boolean
  captchaId?: string
  captchaCode?: string
  captchaImage?: string
  captchaInput?: string
  billInfo?: BillCaptchaCheckRes
}

interface ParsedCaptchaPayload {
  captchaCode?: string
  captchaImage?: string
}

interface BillStatusMessage {
  kind: 'credit' | 'no-payment'
  amount?: string
}

const Step1: FunctionComponent<Props> = ({ onClick, type }) => {
  const { locale } = useLanguage()
  const { showError } = useToast()
  const { translate } = useIntl()
  const { bill } = useMobiApi()

  const [state, setState] = useReducer(
    (currentState: State, newState: State) => ({ ...currentState, ...newState }),
    {
      number: '',
      paymentAmount: '',
      captchaInput: '',
      lookupLoading: false
    }
  )

  const phoneInputLabel = type === 'MOBINET_BILL'
    ? translate('numberInput.hbb')
    : type === 'VOO_BILL'
      ? translate('numberInput.voo')
      : translate('phoneNumberAndContract')

  const captchaTitle = translate('bill.captcha')
  const captchaInputLabel = translate('bill.captcha.placeholder')
  const invalidCaptchaText = translate('bill.captcha.invalid')

  const localizeBackendError = (message?: string): string | undefined => {
    if (message === undefined || locale === 'en') {
      return message
    }

    const normalizedMessage = message.trim().toLowerCase()

    if (normalizedMessage.includes('isdn can\'t be blank')) {
      return translate('error.isdnError')
    }

    if (normalizedMessage.includes('captcha') && normalizedMessage.includes('blank')) {
      return invalidCaptchaText
    }

    if (normalizedMessage.includes('captcha') && (normalizedMessage.includes('invalid') || normalizedMessage.includes('incorrect'))) {
      return invalidCaptchaText
    }

    return message
  }

  const normalizeErrorValue = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
      return value.trim() !== '' ? localizeBackendError(value) : undefined
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const normalizedItem = normalizeErrorValue(item)
        if (normalizedItem !== undefined) {
          return normalizedItem
        }
      }
    }

    if (value !== null && typeof value === 'object') {
      const record = value as Record<string, unknown>
      const nestedMessage =
        normalizeErrorValue(record.message) ??
        normalizeErrorValue(record.code) ??
        normalizeErrorValue(record.error)

      if (nestedMessage !== undefined) {
        return localizeBackendError(nestedMessage)
      }
    }

    return undefined
  }

  const getErrorMessage = (
    res?: { error?: unknown, cause?: { messageEn?: string, messageMn?: string } },
    fallback = translate('error')
  ): string => {
    if (locale === 'en') {
      return res?.cause?.messageEn ?? normalizeErrorValue(res?.error) ?? fallback
    }
    return res?.cause?.messageMn ?? normalizeErrorValue(res?.error) ?? fallback
  }

  const normalizeAmount = (value?: string): string => value?.replace(/,/g, '').trim() ?? ''

  const parseAmount = (value?: string): number | undefined => {
    const normalizedValue = normalizeAmount(value)

    if (normalizedValue === '') {
      return undefined
    }

    const parsedValue = Number(normalizedValue)

    return Number.isFinite(parsedValue) ? parsedValue : undefined
  }

  const getFirstNonEmptyAmount = (...values: Array<string | undefined>): string => {
    for (const value of values) {
      const normalizedValue = normalizeAmount(value)

      if (normalizedValue !== '') {
        return normalizedValue
      }
    }

    return ''
  }

  const resolveBillAmount = (billInfo?: BillCaptchaCheckRes): string => {
    return getFirstNonEmptyAmount(
      billInfo?.bill?.currentBalance,
      billInfo?.currentBalance,
      billInfo?.bill?.openAmount,
      billInfo?.openAmount,
      billInfo?.bill?.accessBillAmount,
      billInfo?.accessBillAmount,
      billInfo?.bill?.billAmount,
      billInfo?.billAmount
    )
  }

  const resolveBillAmountValue = (billInfo?: BillCaptchaCheckRes): number | undefined => {
    return parseAmount(resolveBillAmount(billInfo))
  }

  const resolveInitialPaymentAmount = (billInfo?: BillCaptchaCheckRes): string => {
    const billAmount = resolveBillAmount(billInfo)
    const billAmountValue = parseAmount(billAmount)

    if (billAmountValue === undefined) {
      return ''
    }

    return billAmountValue > 0 ? billAmount : '0'
  }

  const resolveBillStatusMessage = (billInfo?: BillCaptchaCheckRes): BillStatusMessage | undefined => {
    const billAmountValue = resolveBillAmountValue(billInfo)

    if (billAmountValue === undefined) {
      return undefined
    }

    if (billAmountValue < 0) {
      return {
        kind: 'credit',
        amount: moneyFormatter(Math.abs(billAmountValue))
      }
    }

    if (billAmountValue === 0) {
      return { kind: 'no-payment' }
    }

    return undefined
  }

  const resolveBillDate = (billInfo?: BillCaptchaCheckRes): string | undefined => {
    return billInfo?.bill?.billDate ?? billInfo?.billDate
  }

  const resolveCaptchaImage = (): string | undefined => {
    const image = state.captchaImage?.trim()

    if (image === undefined || image === '') {
      return undefined
    }

    if (image.startsWith('data:image')) {
      return image
    }

    return `data:image/png;base64,${image}`
  }

  const extractString = (value: unknown): string | undefined => {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
  }

  const parseCaptchaPayload = (payload: unknown): ParsedCaptchaPayload => {
    if (payload === null || payload === undefined) {
      return {}
    }

    if (typeof payload === 'string') {
      return payload.startsWith('data:image')
        ? { captchaImage: payload }
        : { captchaCode: payload }
    }

    if (typeof payload === 'object') {
      const obj = payload as Record<string, unknown>
      const captchaValue = extractString(obj.captcha)

      return {
        captchaImage: captchaValue?.startsWith('data:image') === true ? captchaValue : undefined,
        captchaCode: captchaValue?.startsWith('data:image') === true ? undefined : captchaValue
      }
    }

    return {}
  }

  const handleNumber = (value: string): void => {
    if (value.length < 9) {
      setState({
        number: value,
        numberError: value.length < 8 ? undefined : state.numberError
      })
    }

    if (value.length === 8) {
      void checkNumber(value, state.checkBill === true && state.lookupStep === 'phone')
    }
  }

  const checkNumber = async (number: string, openCaptchaOnSuccess = false): Promise<void> => {
    try {
      const res = await bill.checkNumber(number, type)

      if (res !== undefined) {
        if (res.success) {
          setState({ numberError: undefined })

          if (openCaptchaOnSuccess) {
            void fetchCaptcha(number)
          }
        }

        if (res.error !== undefined) {
          setState({ numberError: getErrorMessage(res) })
        }
      } else {
        setState({ numberError: undefined })
      }
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (state.paymentAmount !== '' && state.paymentAmount !== undefined && parseFloat(state.paymentAmount) < 1000) {
      setState({ amountError: true })
    } else {
      setState({ amountError: false })
    }
  }, [state.paymentAmount])

  const handlePayment = (value: string): void => {
    setState({ paymentAmount: value })
  }

  const fetchCaptcha = async (numberParam?: string): Promise<void> => {
    const targetNumber = numberParam ?? state.number

    if (targetNumber === undefined || targetNumber.length < 8 || state.numberError !== undefined) {
      return
    }

    setState({ lookupLoading: true })

    try {
      const res = await bill.createCaptcha({ isdn: targetNumber, type })
      const parsedCaptcha = parseCaptchaPayload(res?.captchaCode ?? res?.captcha ?? res?.image)

      if (res?.error !== undefined || res?.success === false) {
        showError(getErrorMessage(res))
        setState({ lookupLoading: false })
        return
      }

      setState({
        lookupStep: 'captcha',
        lookupLoading: false,
        number: targetNumber,
        captchaId: res?.captchaId,
        captchaCode: parsedCaptcha.captchaCode,
        captchaImage: parsedCaptcha.captchaImage ?? res?.captchaImage ?? res?.image,
        captchaInput: ''
      })
    } catch (e) {
      console.error(e)
      showError(translate('error'))
      setState({ lookupLoading: false })
    }
  }

  const handleNext = (): void => {
    const payload: CreateInvoiceReq = {
      isdn: state.number,
      type,
      products: [{
        productCode: `${state.number ?? ''} ${type}`,
        productType: 'BILL',
        price: state.paymentAmount !== undefined ? parseFloat(state.paymentAmount) : 0
      }]
    }

    onClick(payload)
  }

  const openLookupFlow = (): void => {
    if (state.number !== undefined && state.number.length === 8 && state.numberError === undefined) {
      setState({
        checkBill: true,
        lookupStep: 'phone',
        paymentAmount: '',
        amountError: false,
        billInfo: undefined,
        lookupLoading: false,
        captchaId: undefined,
        captchaCode: undefined,
        captchaImage: undefined,
        captchaInput: ''
      })
      void fetchCaptcha(state.number)
      return
    }

    setState({
      checkBill: true,
      lookupStep: 'phone',
      paymentAmount: '',
      amountError: false,
      billInfo: undefined,
      lookupLoading: false,
      captchaId: undefined,
      captchaCode: undefined,
      captchaImage: undefined,
      captchaInput: ''
    })
  }

  const refreshCaptcha = (): void => {
    void fetchCaptcha()
  }

  const handleCaptchaContinue = async (): Promise<void> => {
    if ((state.captchaInput ?? '').trim() === '') {
      return
    }

    setState({ lookupLoading: true })

    try {
      const captchaValue = (state.captchaInput ?? '').trim()
      const res = await bill.checkCaptcha({
        isdn: state.number ?? '',
        type,
        captchaId: state.captchaId,
        captcha: captchaValue,
        code: captchaValue
      })

      if (res?.error !== undefined || res?.success === false || res?.captchaValid === false) {
        showError(getErrorMessage(res, invalidCaptchaText))
        await fetchCaptcha()
        return
      }

      const paymentAmount = resolveInitialPaymentAmount(res)

      setState({
        lookupStep: 'amount',
        lookupLoading: false,
        billInfo: res,
        paymentAmount,
        amountError: paymentAmount !== '' ? parseFloat(paymentAmount) < 1000 : false
      })
    } catch (e) {
      console.error(e)
      showError(translate('error'))
      setState({ lookupLoading: false })
    }
  }

  const renderTextField = (
    value: string | undefined,
    label: string,
    onChange: (value: string) => void,
    extraClassName = '',
    typeProp: 'number' | 'text' = 'number',
    disabled = false
  ): React.JSX.Element => (
    <TextField
      value={value}
      id='outlined-basic'
      label={label}
      variant='outlined'
      type={typeProp}
      disabled={disabled}
      onChange={(e) => { onChange(e.target.value) }}
      className={classNames('w-full rounded-lg border-secondaryMainStroke bg-surfaceInverse text-textHigh md:w-96', extraClassName)}
      InputLabelProps={{
        style: { color: 'var(--color-text-textHigh)' }
      }}
      InputProps={{
        style: {
          color: 'var(--color-text-textHigh)',
          '&::placeholder': {
            color: 'var(--color-text-secondaryHighStroke)'
          }
        } as any
      }}
      sx={{
        '& fieldset': {
          borderRadius: '8px'
        }
      }}
    />
  )

  const renderBillSummary = (): React.JSX.Element | null => {
    if (state.billInfo === undefined) {
      return null
    }

    const rows = [
      {
        label: phoneInputLabel,
        value: state.number
      },
      {
        label: translate('amountDue'),
        value: state.paymentAmount !== undefined && state.paymentAmount !== '' ? `${state.paymentAmount}₮` : undefined
      },
      {
        label: translate('order.date'),
        value: resolveBillDate(state.billInfo)
      }
    ].filter((item) => item.value !== undefined && item.value !== '')

    if (rows.length === 0) {
      return null
    }

    return (
      <div className='mt-4 w-full md:w-96 rounded-xl border border-secondaryMidStroke bg-surfaceWebGrey p-4'>
        <span className='text-textMid text-xs font-bold'>{translate('order.summary')}</span>
        <div className='mt-2 flex flex-col gap-2'>
          {
            rows.map((item) => (
              <div key={item.label} className='flex items-start justify-between gap-4'>
                <span className='text-textMid text-xs md:text-sm font-medium'>{item.label}:</span>
                <span className='text-right text-textHigh text-xs md:text-sm font-bold'>{item.value}</span>
              </div>
            ))
          }
        </div>
      </div>
    )
  }

  const canPressCheck = (): boolean => {
    if (state.lookupLoading === true || state.number === undefined || state.number.length < 8 || state.numberError !== undefined) {
      return false
    }

    if (state.lookupStep === 'captcha') {
      return (state.captchaInput ?? '').trim() !== ''
    }

    return true
  }

  const renderAmountMessage = (): React.JSX.Element => {
    const billStatusMessage = resolveBillStatusMessage(state.billInfo)
    const isVisible = billStatusMessage !== undefined || state.amountError === true

    return (
      <p className={classNames('text-md py-1 pl-2', isVisible ? 'visible' : 'invisible', billStatusMessage !== undefined ? 'text-textMid' : 'text-primaryMain')}>
        {
          billStatusMessage?.kind === 'credit'
            ? (
              <>
                <span className='font-bold'>{translate('bill.creditBalance')}:</span>{' '}
                <span className='font-bold'>{billStatusMessage.amount}</span>
              </>
              )
            : billStatusMessage?.kind === 'no-payment'
              ? translate('bill.noPaymentDue')
              : translate('bill.warning')
        }
      </p>
    )
  }

  const renderCaptchaAnswerField = (): React.JSX.Element => (
    <TextField
      value={state.captchaInput}
      id='bill-captcha-answer'
      placeholder={captchaInputLabel}
      variant='outlined'
      type='text'
      onChange={(e) => { setState({ captchaInput: e.target.value }) }}
      className='mt-4 w-full md:w-96 rounded-2xl bg-surfaceInverse text-textHigh'
      InputLabelProps={{
        shrink: false
      }}
      InputProps={{
        style: {
          color: 'var(--color-text-textHigh)',
          fontSize: '16px',
          borderRadius: '14px'
        }
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '14px',
          minHeight: '62px',
          alignItems: 'center'
        },
        '& .MuiOutlinedInput-input': {
          padding: '0 18px',
          '&::placeholder': {
            color: 'var(--color-text-textMid)',
            opacity: 1
          }
        },
        '& fieldset': {
          borderColor: '#64748B'
        }
      }}
    />
  )

  if (state.checkBill === true) {
    if (state.lookupStep === 'phone') {
      return (
        <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
          <div className='p-4 md:p-8 flex w-full flex-col flex-grow overflow-auto'>
            {renderTextField(state.number, phoneInputLabel, handleNumber)}
            <p className={classNames('text-sm text-[#FF0000] py-1 pl-2', state.numberError !== undefined ? 'visible' : 'invisible')}>{state.numberError}</p>
            <div className='mt-8 w-full md:w-96 self-center'>
              <DarkButton type='outlined' leadingIcon={<Download />} size='sm' fullwidth disabled={!canPressCheck()} onClick={openLookupFlow}>{translate('bill.check')}</DarkButton>
            </div>
          </div>
        </div>
      )
    }

    if (state.lookupStep === 'captcha') {
      return (
        <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
          <div className='p-4 md:p-8 flex w-full flex-col flex-grow overflow-auto'>
            {renderTextField(state.number, phoneInputLabel, handleNumber)}
            <p className={classNames('text-sm text-[#FF0000] py-1 pl-2', state.numberError !== undefined ? 'visible' : 'invisible')}>{state.numberError}</p>
            <div className='mt-4 w-full md:w-96 rounded-[20px] bg-[#FAFAFA] px-2.5 py-2.5 shadow-[0_4px_14px_rgba(0,0,0,0.08)]'>
              <div className='flex items-center justify-between gap-1.5'>
                <div className='flex min-h-[58px] min-w-0 flex-1 items-center justify-start overflow-hidden rounded-[14px] bg-white px-2.5'>
                  {
                    resolveCaptchaImage() !== undefined
                      ? <Image src={resolveCaptchaImage() ?? ''} alt={captchaTitle} width={360} height={46} className='h-auto max-h-[58px] w-full object-contain object-left' style={{ transform: 'translateX(-10px) scale(1.08)', transformOrigin: 'left center' }} unoptimized />
                      : <p className='text-center text-[26px] font-bold tracking-[0.08em] text-[#111111]'>{state.captchaCode}</p>
                  }
                </div>
                <button
                  type='button'
                  aria-label={translate('change')}
                  className='flex h-[32px] w-[32px] items-center justify-center rounded-[12px] bg-[#191919] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50'
                  onClick={refreshCaptcha}
                  disabled={state.lookupLoading === true}
                >
                  <Loop sx={{ fontSize: 20 }} />
                </button>
              </div>
            </div>
            {renderCaptchaAnswerField()}
            <div className='mt-8 w-full md:w-96 self-center'>
              <DarkButton type='outlined' leadingIcon={<Download />} size='sm' fullwidth disabled={!canPressCheck()} onClick={() => { void handleCaptchaContinue() }}>{translate('bill.verify')}</DarkButton>
            </div>
          </div>
          <div className='sticky bottom-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
            <DarkButton
              disabled={state.lookupLoading === true || state.amountError === true || state.numberError !== undefined || state.number === undefined || state.number.length < 8 || state.paymentAmount === '' || state.billInfo === undefined}
              fullwidth
              onClick={handleNext}
            >
              {translate('continue')}
            </DarkButton>
          </div>
        </div>
      )
    }

    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
        <div className='p-4 md:p-8 flex w-full flex-col flex-grow overflow-auto'>
          {renderTextField(state.number, phoneInputLabel, handleNumber)}
          <p className={classNames('text-sm text-[#FF0000] py-1 pl-2', state.numberError !== undefined ? 'visible' : 'invisible')}>{state.numberError}</p>
          {renderBillSummary()}
          {renderTextField(state.paymentAmount, translate('amountDue'), handlePayment, 'mt-4')}
          {renderAmountMessage()}
        </div>
        <div className='sticky bottom-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          <DarkButton
            disabled={state.lookupLoading === true || state.amountError === true || state.numberError !== undefined || state.number === undefined || state.number.length < 8 || state.paymentAmount === ''}
            fullwidth
            onClick={handleNext}
          >
            {translate('continue')}
          </DarkButton>
        </div>
      </div>
    )
  }

  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
      <div className='p-4 md:p-8 flex w-full flex-col flex-grow overflow-auto'>
        {renderTextField(state.number, phoneInputLabel, handleNumber)}
        <p className={classNames('text-sm text-[#FF0000] py-1 pl-2', state.numberError !== undefined ? 'visible' : 'invisible')}>{state.numberError}</p>
        {renderTextField(state.paymentAmount, translate('amountDue'), handlePayment, 'mt-4')}
        {renderAmountMessage()}
        {
          ['MOBICOM_BILL', 'MOBINET_BILL', 'MNP75_BILL', 'VOO_BILL'].includes(type) && (
            <div className='w-full md:w-96'>
              <DarkButton type='outlined' fullwidth size='sm' onClick={openLookupFlow}><span className='flex items-center gap-1'><Download fontSize='small' /><span>{translate('bill.check')}</span></span></DarkButton>
            </div>
          )
        }
      </div>
      <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton
          fullwidth
          disabled={state.amountError === true || state.numberError !== undefined || state.number === undefined || state.number.length < 8 || state.paymentAmount === ''}
          onClick={handleNext}
        >
          {translate('continue')}
        </DarkButton>
      </div>
    </div>
  )
}

export default Step1
